import React, { useEffect, useMemo, useState } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useAuth } from "../../contexts/AuthContext";
import { HelpCircle } from "lucide-react";

/**
 * AppTour - Guided product walkthrough
 * 
 * DEX Standard: Premium minimalism - single floating help button
 * Accessible from anywhere in the app via bottom-right button.
 */
export default function AppTour() {
  const { isAuthenticated } = useAuth();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const steps = useMemo(
    () => [
      {
        target: "body",
        placement: "center",
        title: "Welcome to Scout",
        content:
          "Let's take 30 seconds to show you around. Scout finds companies and people using natural language—just describe what you're looking for.",
        disableBeacon: true,
      },
      {
        target: '[data-tour="global-search-container"]',
        title: "Your search bar",
        content:
          "This is where the magic happens. Type naturally—like you're asking a colleague to find leads.",
        disableBeacon: true,
        placement: "bottom",
      },
      {
        target: '[data-tour="global-search-input"]',
        title: "Natural language search",
        content:
          'Try something like "Series B healthcare startups in Boston" or "CFOs at manufacturing companies".',
        placement: "bottom",
      },
      {
        target: '[data-tour="global-search-submit"]',
        title: "Run your search",
        content:
          "Hit this to start. Results typically appear in under 5 seconds. You can refine anytime.",
        placement: "bottom",
      },
      {
        target: '[data-tour="nav-agents"]',
        title: "Your saved agents",
        content:
          "Every search becomes a reusable agent. Access your history and saved searches here.",
        placement: "right",
      },
      {
        target: "body",
        placement: "center",
        title: "You're all set",
        content:
          "Use the heart icon to save leads you like. Export anytime. Happy hunting!",
      },
    ],
    []
  );

  const onStartTour = () => {
    setIsOpen(false);
    setStepIndex(0);
    setRun(true);
  };

  const callback = (data) => {
    const { status, action, type } = data;

    if (type === EVENTS.STEP_AFTER) {
      setStepIndex((prev) => prev + (action === ACTIONS.PREV ? -1 : 1));
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false);
      try {
        localStorage.setItem("eq-tour-seen", "true");
      } catch { /* ignore storage errors */ }
      try {
        document.documentElement.classList.remove("eq-tour-active");
      } catch { /* ignore DOM errors */ }
    }
  };

  useEffect(() => {
    const handleStartTour = () => onStartTour();
    window.addEventListener('eq:start-tour', handleStartTour);
    return () => window.removeEventListener('eq:start-tour', handleStartTour);
  }, []);

  // Pause UI animations while the tour runs
  useEffect(() => {
    try {
      if (run) {
        document.documentElement.classList.add("eq-tour-active");
      } else {
        document.documentElement.classList.remove("eq-tour-active");
      }
    } catch { /* ignore DOM errors */ }
    return () => {
      try {
        document.documentElement.classList.remove("eq-tour-active");
      } catch { /* ignore DOM errors */ }
    };
  }, [run]);

  if (!isAuthenticated) return null;

  return (
    <>
      {/* Floating Help Button - Bottom Right */}
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsOpen(v => !v)}
          className="p-2.5 bg-white/90 backdrop-blur-sm border border-black/10 text-black/50 rounded-xl shadow-sm hover:bg-white hover:text-black/70 hover:border-black/20 transition-all duration-200"
          title="Help"
        >
          <HelpCircle className="w-4 h-4" />
        </button>

        {isOpen && (
          <div 
            className="absolute bottom-full right-0 mb-2 w-44 rounded-xl border border-black/5 bg-white p-1.5 shadow-lg"
            style={{ animation: 'eq-menu-in 200ms ease-out' }}
          >
            <button
              className="w-full rounded-lg px-3 py-2 text-left text-sm text-black/70 hover:bg-black/5 hover:text-black transition-colors"
              style={{ fontFamily: "'Playfair Display', serif" }}
              onClick={onStartTour}
            >
              Start tour
            </button>
            <button
              className="w-full rounded-lg px-3 py-2 text-left text-xs text-black/40 hover:bg-black/5 hover:text-black/60 transition-colors"
              onClick={() => {
                try {
                  localStorage.removeItem("eq-tour-seen");
                } catch { /* ignore storage errors */ }
                setIsOpen(false);
              }}
            >
              Reset progress
            </button>
          </div>
        )}
      </div>

      {/* Joyride Tour */}
      <Joyride
        steps={steps}
        run={run}
        stepIndex={stepIndex}
        continuous
        showSkipButton
        showProgress={false}
        spotlightClicks={false}
        disableOverlayClose
        disableScrolling
        scrollToFirstStep={false}
        spotlightPadding={10}
        locale={{
          back: "Back",
          close: "Close",
          last: "Finish",
          next: "Next",
          skip: "Skip",
        }}
        floaterProps={{ disableFlip: true, offset: 8 }}
        styles={{
          options: {
            primaryColor: "#7c4dff",
            zIndex: 10000,
            arrowColor: "#ffffff",
            backgroundColor: "#ffffff",
            overlayColor: "rgba(0, 0, 0, 0.4)",
            textColor: "#374151",
          },
          tooltip: {
            borderRadius: 16,
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.15)",
            padding: "20px 24px",
            maxWidth: 340,
          },
          tooltipTitle: {
            fontSize: 15,
            fontWeight: 600,
            color: "#111827",
            marginBottom: 8,
            fontFamily: "'Playfair Display', serif",
          },
          tooltipContent: {
            fontSize: 14,
            lineHeight: 1.6,
            color: "#6b7280",
          },
          tooltipContainer: {
            textAlign: "left",
          },
          spotlight: {
            borderRadius: 12,
          },
          buttonNext: {
            backgroundColor: "rgba(124, 77, 255, 0.1)",
            color: "#7c4dff",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            padding: "8px 16px",
          },
          buttonBack: {
            color: "#9ca3af",
            fontSize: 13,
            marginRight: 8,
          },
          buttonSkip: {
            color: "#d1d5db",
            fontSize: 13,
          },
          buttonClose: {
            color: "#d1d5db",
          },
        }}
        callback={callback}
      />

      <style>{`
        @keyframes eq-menu-in {
          from { opacity: 0; transform: translateY(8px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
}
